import React, { Fragment } from 'react';
import { useTheme } from '@mui/material/styles';
import { Card, CardHeader, CardContent, Divider, Box, IconButton, Tooltip } from '@mui/material';
import { useSelector } from 'src/store/Store';
import { AppState } from 'src/store/Store';
import { IconPlus, IconArrowLeft } from '@tabler/icons';
import { useNavigate } from 'react-router';
import { saveAs } from 'file-saver';
import { utils, write } from 'xlsx';
import Window from '@mui/icons-material/Window';
import { useSnackbar } from 'notistack';

type Props = {
  title: string;
  footer?: string | JSX.Element;
  children: JSX.Element;
  withAdd?: boolean;
  additionRoute?: string;
  navWithState?: string | undefined | null;
  dataToExport?: any;
  fileName?: any;
};

const ParentCard = ({
  title,
  children,
  footer,
  withAdd = false,
  additionRoute = '/',
  navWithState,
  dataToExport = null,
  fileName = null,
}: Props) => {
  const notistack = useSnackbar();
  const navigate = useNavigate();
  const customizer = useSelector((state: AppState) => state.customizer);

  const theme = useTheme();
  const borderColor = theme.palette.divider;

  const throwToast = (title: string, isErr: boolean, isInfo: boolean) => {
    notistack.enqueueSnackbar(title, {
      variant: isErr ? 'error' : isInfo ? 'info' : 'default',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  };

  const exportToExcel = () => {
    throwToast('Exporting data, please wait.', false, true);
    try {
      const worksheet = utils.json_to_sheet(dataToExport);
      const workbook = utils.book_new();
      utils.book_append_sheet(workbook, worksheet, 'Sheet1');
      const excelBuffer = write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
      saveAs(blob, `${fileName}.xlsx`);
    } catch (error) {
      throwToast('Failed to export data, try again.', true, false);
    }
  };

  return (
    <Card
      sx={{ padding: 0, border: !customizer.isCardShadow ? `1px solid ${borderColor}` : 'none' }}
      elevation={customizer.isCardShadow ? 9 : 0}
      variant={!customizer.isCardShadow ? 'outlined' : undefined}
    >
      {withAdd ? (
        <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
          <Box
            display={'flex'}
            flexDirection={'row'}
            justifyContent={'start'}
            alignItems={'center'}
            gap={'10px'}
          >
            <Tooltip title="Go Back">
              <IconButton
                sx={{ height: 50, width: 50 }}
                id="basic-button"
                aria-haspopup="true"
                onClick={() => navigate(-1)}
              >
                <IconArrowLeft width={18} />
              </IconButton>
            </Tooltip>
            <CardHeader title={title} />
          </Box>
          <div>
            {dataToExport !== null ? (
              <Tooltip title="Export Excel">
                <IconButton
                  sx={{ height: 50, width: 50 }}
                  id="basic-button"
                  aria-haspopup="true"
                  onClick={exportToExcel}
                >
                  <Window sx={{ width: 18 }} />
                </IconButton>
              </Tooltip>
            ) : (
              <></>
            )}
            <Tooltip title="Create New">
              <IconButton
                sx={{ height: 50, width: 50 }}
                id="basic-button"
                aria-haspopup="true"
                onClick={() => {
                  navWithState
                    ? navigate(`${additionRoute}`, {
                        state: { _id: navWithState },
                      })
                    : navigate(`${additionRoute}`);
                }}
              >
                <IconPlus width={18} />
              </IconButton>
            </Tooltip>
          </div>
        </Box>
      ) : (
        <Box
          display={'flex'}
          flexDirection={'row'}
          justifyContent={'start'}
          alignItems={'center'}
          gap={'10px'}
        >
          <Tooltip title="Go Back">
            <IconButton
              sx={{ height: 50, width: 50 }}
              id="basic-button"
              aria-haspopup="true"
              onClick={() => navigate(-1)}
            >
              <IconArrowLeft width={18} />
            </IconButton>
          </Tooltip>
          <CardHeader title={title} />
        </Box>
      )}

      <Divider />

      <CardContent>{children}</CardContent>
      {footer ? (
        <>
          <Divider />
          <Box p={3}>{footer}</Box>
        </>
      ) : (
        ''
      )}
    </Card>
  );
};

export default ParentCard;
